/**
 * Sincronização com o servidor da ticketeira.
 *
 * Ordem que não pode inverter: sobe o que foi validado aqui ANTES de aplicar o
 * que veio de fora. Como o servidor faz as duas coisas no mesmo pedido, a
 * resposta 200 é a confirmação — só depois dela as validações locais são
 * marcadas como enviadas.
 */

import {sincronizarCodigos, type PacoteSincronizacao} from '../api/cliente';
import {formatarDataHora} from '../api/protocolo';
import {atualizarUsuariosAutorizados} from '../autorizacao';
import {
  definirUltimaAtualizacao,
  marcarComoEnviados,
  salvarCancelados,
  salvarIngressosOnline,
  salvarLotes,
  salvarSetores,
  salvarValidadosDoServidor,
  ultimaAtualizacao,
  validadosPendentes,
} from '../dados/repositorio';

export interface ProgressoSync {
  etapa: string;
  concluido: boolean;
}

export interface ResultadoSync {
  enviados: number;
  lotes: number;
  setores: number;
  cancelados: number;
  ingressos: number;
  validadosDeOutrosAparelhos: number;
}

type Ouvinte = (progresso: ProgressoSync) => void;

async function aplicarPacote(
  eventoId: number,
  pacote: PacoteSincronizacao,
  avisar: Ouvinte,
): Promise<void> {
  const agora = new Date().toISOString();

  avisar({etapa: 'Cancelamentos', concluido: false});
  await salvarCancelados(pacote.cancelados);

  avisar({etapa: 'Validações de outros aparelhos', concluido: false});
  await salvarValidadosDoServidor(eventoId, pacote.validadosNoServidor, agora);

  avisar({etapa: 'Lotes', concluido: false});
  await salvarLotes(pacote.lotes);

  avisar({etapa: 'Setores', concluido: false});
  await salvarSetores(pacote.setores);

  avisar({etapa: 'Ingressos', concluido: false});
  await salvarIngressosOnline(pacote.ingressosOnline);
}

/**
 * O cursor de quem nunca sincronizou. O servidor devolve o evento inteiro.
 *
 * É o mesmo valor que o banco guarda antes da primeira carga (ver o padrão de
 * `sync_estado` em src/dados/esquema.ts): pedir daqui é literalmente repetir a
 * abertura do evento.
 */
const INICIO_DOS_TEMPOS = '2000-01-01';

/**
 * Ciclo normal: manda o que está pendente e traz o delta desde o último sync.
 * Barato o suficiente para rodar a cada poucos minutos durante o evento.
 *
 * `doZero` ignora o cursor e pede o evento inteiro — é o botão de atualizar.
 */
export async function sincronizarEvento(
  eventoId: number,
  machineId: number,
  avisar: Ouvinte = () => {},
  doZero = false,
): Promise<ResultadoSync> {
  avisar({etapa: 'Enviando validações', concluido: false});

  const pendentes = await validadosPendentes(eventoId);
  const desde = doZero ? INICIO_DOS_TEMPOS : await ultimaAtualizacao(eventoId);

  const pacote = await sincronizarCodigos({
    eventoId,
    machineId,
    desde,
    pendentes,
  });

  // A resposta chegou: o servidor recebeu os pendentes. Só agora eles saem da fila.
  await marcarComoEnviados(pendentes.map(v => v.codbar));

  await aplicarPacote(eventoId, pacote, avisar);

  // Preferir o relógio do servidor: se o do aparelho estiver adiantado, o
  // próximo delta pularia validações feitas nesse intervalo.
  const proximoCursor = pacote.datahora ?? formatarDataHora(new Date());
  await definirUltimaAtualizacao(eventoId, proximoCursor);

  // O que o delta trouxe, ciclo a ciclo.
  //
  // Mesmo motivo do log de `[eventos]`: quando alguém diz "vendi agora e não
  // apareceu no validador", só isto separa "o servidor não mandou" de "o app
  // não gravou". Sem ele a conversa vira palpite dos dois lados. Sem query
  // string, como manda a casa — o cursor é data, não segredo.
  console.log(
    `[sync] desde=${desde} → online=${pacote.ingressosOnline.length}` +
      ` lotes=${pacote.lotes.length} setores=${pacote.setores.length}` +
      ` cancelados=${pacote.cancelados.length}` +
      ` validadosDeOutros=${pacote.validadosNoServidor.length}` +
      ` enviados=${pendentes.length} proximoCursor=${proximoCursor}`,
  );

  avisar({etapa: 'Pronto', concluido: true});

  return {
    enviados: pendentes.length,
    lotes: pacote.lotes.length,
    setores: pacote.setores.length,
    cancelados: pacote.cancelados.length,
    ingressos: pacote.ingressosOnline.length,
    validadosDeOutrosAparelhos: pacote.validadosNoServidor.length,
  };
}

/**
 * Carga do evento: o delta, que traz lotes, setores, cancelados e os ingressos
 * nominais desde a última vez.
 *
 * `cargaCompleta` joga o cursor fora e pede tudo de novo — é o botão de
 * atualizar. Ele existe porque o delta do servidor não traz venda feita depois
 * da abertura: quem vendeu um ingresso nominal com a portaria já rodando não
 * via o portador aparecer, e a única coisa que funcionava era fechar e abrir o
 * evento. Pedir do começo é repetir exatamente o caminho da abertura, que
 * comprovadamente traz tudo.
 *
 * Antes isto era feito pela ação `downloadOnline`, e não funcionava: o servidor
 * estoura a memória do PHP nela, a falha leva dez segundos para chegar e era
 * engolida num aviso de console — a tela dizia "Carregamento concluído" sem ter
 * trazido nada. O caminho novo não usa aquela ação.
 */
export async function prepararEvento(
  eventoId: number,
  machineId: number,
  avisar: Ouvinte = () => {},
  cargaCompleta = false,
): Promise<ResultadoSync> {
  const resultado = await sincronizarEvento(eventoId, machineId, avisar, cargaCompleta);

  // Lista de quem pode liberar operação restrita: baixada agora, com rede, para
  // que a portaria consiga autorizar depois mesmo sem sinal.
  try {
    avisar({etapa: 'Atualizando acessos', concluido: false});
    const quantos = await atualizarUsuariosAutorizados();
    console.log(`[sync] ${quantos} usuários com acesso guardados`);
  } catch (erro) {
    console.warn(
      `[sync] lista de acessos não atualizada: ${(erro as Error).message}`,
    );
  }

  avisar({etapa: 'Pronto para validar', concluido: true});

  return resultado;
}
